import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  DataList,
  Flex,
  Grid,
  Separator,
  Select,
  Table,
  Tabs,
  Text,
  TextField,
  Tooltip,
} from '@radix-ui/themes'
import { ArrowLeftIcon, ExternalLinkIcon, PlusIcon, XIcon } from 'lucide-react'

import useDealFetcher from '../hooks/useDealFetcher'
import Deal from '../interfaces/Deal'
import statusCreditTranslate from '../utils/statusCreditTranslate'
import { formatCurrency, formatIsoToDDMMYYYY, formatWithThousandSep } from '../utils/utils'
import { Rule, validateFields } from '../utils/validateForm'
import contactedViaDealTranslate from '../utils/contactedViaDealTranslate'
import dealCancelReasonTranslate, { transDealCancelReason } from '../utils/dealCancelReasonTranslate'
import useDealCancel from '../hooks/useDealCancel'
import useDealRestore from '../hooks/useDealRestore'
import useUserProfileFetcher from '../modules/users/hooks/useUserProfileFetcher'
import { UserProfileDto } from '../modules/users/types/UserProfileDto'
import { useAuth } from '../hooks/useAuth'
import useDealAssign from '../hooks/useDealAssign'
import useCreditFetcher from '../hooks/useCreditFetcher'
import { ScreenWrapper } from '../modules/common/components/ScreenWrapper'
import { AppPath } from '../enums/AppPath'
import { mapCreditTypeToBusinessType } from '../utils/autofin'

import DisplayError from './DisplayError'
import { DocStatusHeaderEx } from './DocStatusHeader'
import CheckboxField from './CheckBoxField'
import InputTextArea from './InputTextArea'
import { SelectField } from './SelectField'
import CreditEdit, { CreditFormat } from './CreditEdit'
import { DisplayValueFormat } from './DisplayValue'
import { ButtonAction, ButtonCheckConfirma } from './Buttons'
import { ClientCard } from './ClientCard'
import VehicleCard from './VehicleCard'
import DealActivity from './DealActivity'
import DealComplete from './DealComplete'
import AutoFinIframe from './AutoFinIframe'
import { AutoFinHistory } from './AutoFinHistory'

type FormElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

const DealDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { fetchDealDetail, errorDeal } = useDealFetcher()
  const [deal, setDeal] = useState<Deal>()
  const [creaCreditSw, setCreaCreditSw] = useState<number>(-1)
  const [refreshKey, setRefreshKey] = useState(0)
  const [openOption, setOpenOption] = useState<number>(0)
  const [credit, setCredit] = useState<CreditFormat | null>(null)
  const { fetchCreditFormatDetail, errorCredit } = useCreditFetcher()
  const [showAutoFin, setShowAutoFin] = useState<boolean>(false)
  // AutoFin credit type selection: 0 = Convencional, 1 = Compra inteligente (Autorenuevate)
  const [autofinCreditType, setAutofinCreditType] = useState<number>(0)
  // AutoFin custom price (undefined = use vehicle price)
  const [customPrice, setCustomPrice] = useState<number | undefined>(undefined)
  // Display value for price input (formatted string vs raw number)
  const [priceInputValue, setPriceInputValue] = useState<string>('')
  const [isPriceFocused, setIsPriceFocused] = useState<boolean>(false)

  // Get navigation state for back button logic
  const navState = location.state as { from?: string; creditId?: string } | null

  useEffect(() => {
    fetchAsync()
  }, [id, refreshKey])

  const fetchAsync = async () => {
    if (!id) {
      return // Exit early if `id` is undefined
    }

    const deal = await fetchDealDetail(id)

    if (errorDeal || !deal) return
    setDeal(deal)
  }
  const handleRefresh = () => setRefreshKey((prev) => prev + 1) // Increment the refreshKey to trigger re-fetch
  const reason = deal?.cancelledReason ? transDealCancelReason(deal?.cancelledReason) : ''

  const handleCrearCredit = async (id: string) => {
    // Creando
    if (!id) {
      setCreaCreditSw(1)

      return
    }

    // Modificando
    setCreaCreditSw(0)
    const credit = await fetchCreditFormatDetail(id)

    if (errorCredit || !credit) return
    setCredit(credit)
  }
  const onCloseCredit = () => {
    setCreaCreditSw(-1)
    handleRefresh()
    // Abrimos el crédito
    setOpenOption(1)
  }

  const handleBackClick = () => {
    if (navState?.from === 'credits') {
      navigate(`/${AppPath.Loans}`)
    } else if (navState?.from === 'credit-edit' && navState.creditId) {
      navigate(`/${AppPath.Loans}/edit/${navState.creditId}`)
    } else {
      navigate(`/${AppPath.Deals}`)
    }
  }

  const getBackLabel = () => {
    if (navState?.from === 'credits') return 'Volver a Créditos'

    if (navState?.from === 'credit-edit') return 'Volver a Solicitud'

    return 'Volver a Negocios'
  }

  return (
    <ScreenWrapper>
      {/* Back Button */}
      <div className="mb-4">
        <Button variant="soft" color="gray" onClick={handleBackClick} className="flex items-center gap-2">
          <ArrowLeftIcon size={16} />
          {getBackLabel()}
        </Button>
      </div>

      {deal?.cancelledAt && (
        <DocStatusHeaderEx
          title="Este negocio fue cancelado"
          date={deal?.cancelledAt}
          cause={`${reason} : ${deal?.cancelledCause}`}
          by={deal?.cancelledBy_user}
        />
      )}

      {creaCreditSw === 1 && deal && (
        <CreditEdit
          vehicle={deal.body.vehicle}
          client={deal.body.client}
          modificaSw={false}
          vehicleId={deal.vehicle}
          clientId={deal.client}
          dealId={deal._id}
          onClose={onCloseCredit}
        />
      )}
      {creaCreditSw === 0 && deal && credit && (
        <CreditEdit
          vehicle={deal.body.vehicle}
          client={deal.body.client}
          creditIn={credit}
          modificaSw={true}
          vehicleId=""
          clientId=""
          dealId=""
          onClose={onCloseCredit}
        />
      )}

      {creaCreditSw === -1 && deal && (
        <>
          <Grid columns={{ sm: '1', lg: '2', xl: '3' }} gap="4" mb="4">
            <VehicleCard vehicle={deal.body.vehicle} />
            <ClientCard client={deal.body.client} />
            <Card>
              <DataList.Root>
                <DataList.Item>
                  <DataList.Label>Vía de contacto</DataList.Label>
                  <DataList.Value>{contactedViaDealTranslate[deal.contactedVia]}</DataList.Value>
                  <DataList.Label>Responsable</DataList.Label>
                  <DataList.Value>{deal.profile_user}</DataList.Value>
                </DataList.Item>
              </DataList.Root>
            </Card>
          </Grid>

          {deal.closedAt && (
            <div className="max-w-xl mx-auto">
              <div className="text-2xl">Nota de venta N° {deal.body.salenote?.number}</div>
              <div className="grid grid-cols-2 p-2 max-w-xl mx-auto">
                <DisplayValueFormat
                  label="Fecha de aprobación"
                  value={formatIsoToDDMMYYYY(deal.body.salenote?.approvedAt)}
                />
                <DisplayValueFormat label="Precio" value={formatCurrency(deal.body.salenote?.price ?? 0)} />
              </div>
            </div>
          )}
          <SubMenu
            handleCrearCredit={handleCrearCredit}
            deal={deal}
            onRefresh={handleRefresh}
            openOption={openOption}
            onShowAutoFin={() => setShowAutoFin(true)}
            autofinCreditType={autofinCreditType}
            setAutofinCreditType={setAutofinCreditType}
            customPrice={customPrice}
            setCustomPrice={setCustomPrice}
            priceInputValue={priceInputValue}
            setPriceInputValue={setPriceInputValue}
            isPriceFocused={isPriceFocused}
            setIsPriceFocused={setIsPriceFocused}
          />
        </>
      )}
      <div>{errorDeal?.message ?? ''}</div>

      {/* AutoFin Iframe */}
      {deal && (
        <AutoFinIframe
          vehicle={deal.body.vehicle}
          client={deal.body.client}
          businessType={mapCreditTypeToBusinessType(autofinCreditType)}
          customPrice={customPrice}
          idExterno={deal._id}
          isOpen={showAutoFin}
          onClose={() => setShowAutoFin(false)}
          onLoad={() => console.log('AutoFin iframe loaded')}
          onError={(error) => console.error('AutoFin error:', error)}
        />
      )}
    </ScreenWrapper>
  )
}

const SubMenu: React.FC<{
  handleCrearCredit: (id: string) => void
  deal: Deal
  onRefresh: () => void
  openOption: number
  onShowAutoFin: () => void
  autofinCreditType: number
  setAutofinCreditType: (type: number) => void
  customPrice?: number
  setCustomPrice: (price: number | undefined) => void
  priceInputValue: string
  setPriceInputValue: (value: string) => void
  isPriceFocused: boolean
  setIsPriceFocused: (focused: boolean) => void
}> = ({
  handleCrearCredit,
  deal,
  onRefresh,
  onShowAutoFin,
  autofinCreditType,
  setAutofinCreditType,
  customPrice,
  setCustomPrice,
  priceInputValue,
  setPriceInputValue,
  isPriceFocused,
  setIsPriceFocused,
}) => {
  const { userData } = useAuth()

  // Organization-based control: Only checks organization setting
  const autofinEnabled = userData?.organization?.external?.autofin?.enabled ?? false

  useEffect(() => {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })
  }, [])

  // Se usa en Reasignar y Completar
  const onRefreshComplete = () => {
    onRefresh()
  }

  const handleRowCreditClick = async (key: string) => {
    const creditAux = deal.body.credits.find((x) => x._id === key)

    if (!creditAux) {
      return
    }

    handleCrearCredit(creditAux._id)
  }

  return (
    <>
      <Tabs.Root defaultValue="deal-history">
        <Tabs.List mb="4">
          <Tabs.Trigger value="deal-history">Historial</Tabs.Trigger>
          <Tabs.Trigger value="deal-loans">Solicitud de Crédito</Tabs.Trigger>
          {!deal.closedAt && <Tabs.Trigger value="deal-complete">Completar</Tabs.Trigger>}
          <Tabs.Trigger value="deal-assign">Reasignar</Tabs.Trigger>
          <Tabs.Trigger value="deal-cancel-restore">{deal.cancelledAt ? 'Restaurar' : 'Cancelar'}</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="deal-history">
          <Card>
            <DealActivity deal={deal} onRefresh={onRefresh} />
          </Card>
        </Tabs.Content>
        <Tabs.Content value="deal-loans">
          <Card mb="4">
            <Flex align="center" justify="end" gap="4" wrap="wrap">
              {autofinEnabled && (
                <>
                  <img src="/images/autofin-black.svg" style={{ height: '1em' }} />

                  <Flex align="center" gap="3" wrap="wrap">
                    {/* Credit Type Selector */}
                    <Flex align="center" gap="2">
                      <Text size="2">Tipo:</Text>
                      <Select.Root
                        value={autofinCreditType.toString()}
                        onValueChange={(value) => setAutofinCreditType(Number(value))}
                      >
                        <Select.Trigger placeholder="Tipo" />
                        <Select.Content>
                          <Select.Item value="0">Convencional</Select.Item>
                          <Select.Item value="1">Autorenuevate</Select.Item>
                        </Select.Content>
                      </Select.Root>
                    </Flex>

                    {/* Price Input */}
                    <Flex align="center" gap="2">
                      <Text size="2">Precio:</Text>
                      <Tooltip content="Cambiar precio">
                        <Flex align="center" gap="1">
                          <TextField.Root
                            value={
                              isPriceFocused
                                ? priceInputValue
                                : formatWithThousandSep(customPrice ?? (deal.body.vehicle?.price || 0))
                            }
                            onChange={(e) => {
                              const rawValue = e.target.value.replace(/\D/g, '') // Remove non-digits
                              setPriceInputValue(rawValue)
                              const numValue = rawValue ? parseInt(rawValue, 10) : undefined
                              setCustomPrice(numValue)
                            }}
                            onFocus={() => {
                              setIsPriceFocused(true)
                              setPriceInputValue((customPrice ?? (deal.body.vehicle?.price || 0)).toString())
                            }}
                            onBlur={() => {
                              setIsPriceFocused(false)
                              setPriceInputValue(formatWithThousandSep(customPrice ?? (deal.body.vehicle?.price || 0)))
                            }}
                            type="text"
                            placeholder={formatWithThousandSep(deal.body.vehicle?.price || 0)}
                            style={{
                              width: '140px',
                              borderColor:
                                customPrice !== undefined && customPrice !== (deal.body.vehicle?.price || 0)
                                  ? 'orange'
                                  : undefined,
                            }}
                          >
                            <TextField.Slot side="left">
                              <Text color="gray">$</Text>
                            </TextField.Slot>
                            {customPrice !== undefined && customPrice !== (deal.body.vehicle?.price || 0) && (
                              <TextField.Slot side="right">
                                <Button
                                  variant="ghost"
                                  size="1"
                                  onClick={() => {
                                    setCustomPrice(undefined)
                                    setPriceInputValue('')
                                  }}
                                  style={{ padding: 0, height: '16px', width: '16px' }}
                                >
                                  <XIcon size={12} />
                                </Button>
                              </TextField.Slot>
                            )}
                          </TextField.Root>
                          {/* Visual indicator for custom price */}
                          {customPrice !== undefined && customPrice !== (deal.body.vehicle?.price || 0) && (
                            <Badge color="orange" size="1">
                              Personalizado
                            </Badge>
                          )}
                        </Flex>
                      </Tooltip>
                    </Flex>

                    <Button onClick={onShowAutoFin} color="yellow">
                      <Text>Evaluar</Text>
                      <ExternalLinkIcon />
                    </Button>
                    <Separator orientation="vertical" />
                  </Flex>
                </>
              )}
              <Button onClick={() => handleCrearCredit('')}>
                <PlusIcon />
                <Text>Crear</Text>
              </Button>
            </Flex>
          </Card>

          {(deal.body.credits?.length ?? 0) > 0 && (
            <Card mb="4">
              <Table.Root>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>Estado</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Institución</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell align="right">Monto</Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {(deal.body.credits ?? []).map((field) => (
                    <Table.Row
                      onClick={() => handleRowCreditClick(field._id)}
                      key={field._id}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      <Table.Cell>{statusCreditTranslate[field.status] || field.status}</Table.Cell>
                      <Table.Cell>{field.institution}</Table.Cell>
                      <Table.Cell className="text-right">{formatCurrency(field.amount)}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Card>
          )}

          <AutoFinHistory autofin={deal.autofin} />
        </Tabs.Content>
        <Tabs.Content value="deal-complete">
          <Card>
            <DealComplete deal={deal} onRefresh={onRefreshComplete} />
          </Card>
        </Tabs.Content>
        <Tabs.Content value="deal-assign">
          <Card>
            <DealAssign deal={deal} onRefresh={onRefreshComplete} />
          </Card>
        </Tabs.Content>
        <Tabs.Content value="deal-cancel-restore">
          <Card>
            {deal.cancelledAt ? (
              <DealRestore id={deal._id} onRefresh={onRefresh} />
            ) : (
              <DealCancel id={deal._id} onRefresh={onRefresh} />
            )}
          </Card>
        </Tabs.Content>
      </Tabs.Root>
    </>
  )
}

// Cancela el negocio
const DealCancel: React.FC<{ id: string; onRefresh: () => void }> = ({ id, onRefresh }) => {
  const [motivo, setMotivo] = useState<string>('') // El texto
  const [reason, setReason] = useState<string>('') // El codigo del motivo
  const { dealCancel, dealCancelError } = useDealCancel()
  const handleInputChange = (e: React.ChangeEvent<FormElement>) => {
    const { name, value } = e.target

    if (name === 'motivo') {
      setMotivo(value)
    } else if (name === 'reason') {
      setReason(value)
    }
  }

  const handleCancelarNegocio = async (motivo: string, reason: string) => {
    await dealCancel(id, motivo, reason)
    onRefresh()
  }

  return (
    <>
      <ButtonCheckConfirmaWcombo
        title="Cancelar negocio"
        subTitle="Al cancelar el negocio ya no podrá ser reasignado."
        checkLabel="Estoy seguro de cancelar este negocio."
        buttonLabel="Cancelar negocio"
        handleCrearClick={() => handleCancelarNegocio(motivo, reason)}
        handleTextAreaChange={handleInputChange}
        textAreaLabel={`Notas`}
        textAreaName="motivo"
        textAreaValue={motivo}
        comboValue={reason}
      />
      <div>{dealCancelError?.message}</div>
    </>
  )
}
const ButtonCheckConfirmaWcombo: React.FC<{
  title: string
  subTitle: string
  checkLabel: string
  buttonLabel: string
  handleCrearClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  handleTextAreaChange: (e: React.ChangeEvent<FormElement>) => void
  textAreaLabel?: string
  textAreaName: string
  textAreaValue: string
  comboValue: string
}> = ({
  title,
  subTitle,
  checkLabel,
  buttonLabel,
  handleCrearClick,
  handleTextAreaChange,
  textAreaLabel,
  textAreaName,
  textAreaValue,
  comboValue,
}) => {
  const [check, setCheck] = useState<boolean>(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  // Handle checkbox change
  const handleCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target
    setCheck(checked)
  }

  const handleCrearClickInt = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!check) return

    const reviewArea = textAreaValue?.length > 0
    const reviewReason = comboValue.length > 0
    const fields = []

    if (!reviewArea && comboValue === 'REASON.OTHER') {
      fields.push({ name: textAreaName, value: textAreaValue, rule: Rule.REQUIRED })
    }

    if (!reviewReason) {
      fields.push({ name: 'reason', value: comboValue, rule: Rule.REQUIRED })
    }

    if (fields.length > 0) {
      const validationErrors = validateFields(fields)

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors) // Set errors if any

        return
      }

      setErrors({})
    }
    handleCrearClick(e)
  }

  return (
    <>
      <div className="mb-2 mt-4">
        <span className="font-bold text-xl">{title}</span>
      </div>
      <div>{subTitle}</div>
      <div className="grid sm:grid-cols-1 lg:grid-cols-2 gap-4">
        <SelectField
          label="Motivo"
          name="reason"
          value={comboValue}
          options={dealCancelReasonTranslate}
          handleInputChange={handleTextAreaChange}
          errors={errors}
        />

        <div className="mb-2">
          <InputTextArea
            label={textAreaLabel ?? ''}
            name={textAreaName ?? ''}
            value={textAreaValue}
            handleInputChange={handleTextAreaChange}
            errors={errors}
          />
        </div>
      </div>
      <div className="border border-gray-300 p-4 rounded-md shadow-sm">
        <CheckboxField
          name="aceptar"
          value=""
          label={checkLabel}
          checked={check}
          handleInputChange={handleCheckChange}
        />
      </div>
      <div className="flex justify-center mt-1">
        <ButtonAction
          label={buttonLabel}
          handleButtonClick={handleCrearClickInt}
          disabled={!check} // Disable the button when not checked
        />
      </div>
    </>
  )
}
// Restablece el negocio
const DealRestore: React.FC<{ id: string; onRefresh: () => void }> = ({ id, onRefresh }) => {
  const { dealRestore, dealRestoreError } = useDealRestore()

  const handleRestaurarNegocio = async () => {
    await dealRestore(id)
    onRefresh()
  }

  return (
    <>
      <ButtonCheckConfirma
        title="Restaurar negocio"
        subTitle="Al restaurar el negocio quedará activo."
        checkLabel="Estoy seguro de restaurar este negocio."
        buttonLabel="Restaurar negocio"
        handleCrearClick={() => handleRestaurarNegocio()}
      />
      <DisplayError msg={dealRestoreError?.message} />
    </>
  )
}

export const ButtonCheckConfirmaEx2: React.FC<{
  title: string
  subTitle: string
  checkLabel: string
  buttonLabel: string
  handleCrearClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  handleTextAreaChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void
  textAreaLabel?: string
  textAreaName?: string
  textAreaValue?: string
  check: boolean
  handleCheckChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  btnDisabled: boolean
}> = ({
  title,
  subTitle,
  checkLabel,
  buttonLabel,
  handleCrearClick,
  handleTextAreaChange,
  textAreaLabel,
  textAreaName,
  textAreaValue,
  check,
  handleCheckChange,
  btnDisabled,
}) => {
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  const handleCrearClickInt = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!check) return

    if (handleTextAreaChange && textAreaName && textAreaValue?.length == 0) {
      // Colocamos en rojo
      const fields = [{ name: textAreaName, value: textAreaValue, rule: Rule.REQUIRED }]

      const validationErrors = validateFields(fields)

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors) // Set errors if any

        return
      }

      setErrors({})
    }
    handleCrearClick(e)
  }

  return (
    <>
      <div className="mb-2 mt-4">
        <span className="font-bold text-xl">{title}</span>
      </div>
      <div>{subTitle}</div>
      {handleTextAreaChange && (
        <div className="mb-2">
          <InputTextArea
            label={textAreaLabel ?? ''}
            name={textAreaName ?? ''}
            value={textAreaValue}
            handleInputChange={handleTextAreaChange}
            errors={errors}
          />
        </div>
      )}

      <div className="border border-gray-300 p-4 rounded-md shadow-sm">
        <CheckboxField
          name="aceptar"
          value=""
          label={checkLabel}
          checked={check}
          handleInputChange={handleCheckChange}
        />
      </div>
      <div className="flex justify-center mt-1">
        <ButtonAction
          label={buttonLabel}
          handleButtonClick={handleCrearClickInt}
          disabled={btnDisabled} // Disable the button when not checked
        />
      </div>
    </>
  )
}
// Reasigna el negocio. Muestra una lista usuarios activos
// para seleccionar uno
const DealAssign: React.FC<{ deal: Deal; onRefresh: () => void }> = ({ deal, onRefresh }) => {
  const { userData } = useAuth()
  const [users, setUsers] = useState<UserProfileDto[] | null>(null)
  const { fetchUserProfiles, errorUserProfile } = useUserProfileFetcher()
  const { dealAssign, dealAssignError } = useDealAssign()
  const [selectedProfileId, setSelectedProfileId] = useState<string>() // State to track the selected button
  const [btnDisabled, setBtnDisabled] = useState<boolean>(true)
  const [confirmaCheck, setConfirmaCheck] = useState<boolean>(false)

  useEffect(() => {
    fetchAsync(userData?.organization?._id ?? '', 0)
  }, [])

  const fetchAsync = async (orgId: string, status: number) => {
    const userProfile: UserProfileDto[] = await fetchUserProfiles(orgId, status)

    if (!userProfile) return
    setUsers(userProfile)
    setSelectedProfileId(deal.profile)
  }

  // Click en alguno de los option de los clientes
  const setRadioSelected = (id: string) => {
    setSelectedProfileId(id)

    if (confirmaCheck) {
      setBtnDisabled(false)
    }
  }

  // Handle checkbox change
  const handleCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target
    setConfirmaCheck(checked)
    setBtnDisabled(!checked || !selectedProfileId)
  }
  const handleAsignarNegocio = async () => {
    await dealAssign(deal._id, selectedProfileId!)
    onRefresh()
  }

  if (!users) return

  return (
    <>
      {users.length === 0 && <div className="text-2xl">No hay usuarios activos. </div>}
      {users.length > 0 && (
        <div>
          <div className="text-2xl">Usuarios activos. </div>
          <div className="grid grid-cols-4 font-bold mt-2">
            <div>Nombre</div>
            <div>Selecccionar</div>
          </div>
          {users.map((field) => (
            <div key={field.user._id} className="grid grid-cols-4">
              <div>
                {field.user.firstname} {field.user.lastname}{' '}
              </div>

              <div>
                <input
                  type="radio"
                  name="userOption"
                  value={field.profile._id}
                  checked={selectedProfileId === field.profile._id}
                  onChange={() => setRadioSelected(field.profile._id)}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
              </div>
            </div>
          ))}

          <ButtonCheckConfirmaEx2
            title="Reasignar negocio"
            subTitle="Al reasignar el negocio el usuario actual podría no tener acceso a este, dependiendo de su perfil."
            checkLabel="Estoy seguro de reasignar este negocio."
            buttonLabel="Reasignar"
            handleCrearClick={() => handleAsignarNegocio()}
            check={confirmaCheck}
            handleCheckChange={handleCheckChange}
            btnDisabled={btnDisabled}
          />
        </div>
      )}
      <div>{errorUserProfile?.message}</div>
      <div>{dealAssignError?.message}</div>
    </>
  )
}
export default DealDetailPage
